import { ReactNode, useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Home, Search, Star, History, Settings, MessageSquare, FileText, FolderKanban } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { backButton } from '@tma.js/sdk-react'
import { trackPageView } from '@/lib/analytics'
import clsx from 'clsx'

interface LayoutProps {
  children: ReactNode
}

type Section = 'laws' | 'assistant'

type NavItem = {
  key: string
  path: string
  icon: LucideIcon
  label: string
  panel?: 'history'
}

const lawNavItems: NavItem[] = [
  { key: 'home', path: '/', icon: Home, label: 'Главная' },
  { key: 'search', path: '/search', icon: Search, label: 'Поиск' },
  { key: 'favorites', path: '/favorites', icon: Star, label: 'Избранное' },
  { key: 'history', path: '/history', icon: History, label: 'История' },
  { key: 'settings', path: '/settings', icon: Settings, label: 'Ещё' },
]

const assistantNavItems: NavItem[] = [
  { key: 'chat', path: '/chat', icon: MessageSquare, label: 'Чат' },
  { key: 'projects', path: '/projects', icon: FolderKanban, label: 'Проекты' },
  { key: 'chat-history', path: '/chat', panel: 'history', icon: History, label: 'История' },
  { key: 'documents', path: '/documents', icon: FileText, label: 'Документы' },
  { key: 'settings', path: '/settings', icon: Settings, label: 'Настройки' },
]

function isAssistantPath(pathname: string): boolean {
  return pathname === '/chat'
    || pathname === '/documents'
    || pathname === '/projects'
    || pathname.startsWith('/projects/')
}

const SECTION_KEY = 'miniapp_section'

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const [section, setSection] = useState<Section>(() => {
    try { return (localStorage.getItem(SECTION_KEY) as Section) || 'laws' } catch { return 'laws' }
  })

  const switchSection = useCallback((s: Section) => {
    setSection(s)
    try { localStorage.setItem(SECTION_KEY, s) } catch { /* ignore */ }
    window.scrollTo(0, 0)
    if (s === 'laws') navigate('/')
    else navigate('/chat')
  }, [navigate])

  useEffect(() => {
    if (isAssistantPath(location.pathname)) {
      setSection('assistant')
    } else if (location.pathname === '/settings') {
      // settings доступен из обоих разделов — не меняем section
    } else {
      setSection('laws')
    }
  }, [location.pathname])

  // Просмотр экрана в Яндекс.Метрике при каждой смене маршрута (SPA = pushState).
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location.pathname, location.search])

  useEffect(() => {
    const isRoot = location.pathname === '/' || location.pathname === '/chat'
    try {
      if (isRoot) {
        backButton.hide()
      } else {
        backButton.show()
      }
      const off = backButton.onClick(() => navigate(-1))
      return () => { off() }
    } catch {
      // backButton not available outside Telegram
    }
  }, [location.pathname, navigate])

  const navItems = section === 'assistant' ? assistantNavItems : lawNavItems
  const showSegment = !location.pathname.startsWith('/law/')
  const currentPanel = new URLSearchParams(location.search).get('panel')

  return (
    <div
      className="app-shell flex flex-col flex-1 min-h-0 overflow-hidden text-telegram-text"
      style={{ paddingTop: 'calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px))' }}
    >
      {/* Segmented Control */}
      {showSegment && (
        <div className="segment-wrapper sticky top-0 z-30" style={{ padding: '10px 16px 6px' }}>
          <div className="glass-segment">
            <div className="grid grid-cols-2">
              <button
                onClick={() => switchSection('laws')}
                className={clsx('glass-segment-item', section === 'laws' ? 'glass-segment-active' : 'glass-segment-inactive')}
              >
                Законы
              </button>
              <button
                onClick={() => switchSection('assistant')}
                className={clsx('glass-segment-item', section === 'assistant' ? 'glass-segment-active' : 'glass-segment-inactive')}
              >
                Ассистент
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="main-content flex-1 min-h-0 overflow-y-auto page-transition" style={{ paddingBottom: 'calc(68px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
        {children}
      </main>

      {/* Bottom Navigation — Liquid Glass */}
      <nav
        className="bottom-nav fixed bottom-0 left-0 right-0 z-20 glass-nav"
        style={{
          paddingBottom: 'var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
          borderRadius: '20px 20px 0 0',
        }}
      >
        <div className="bottom-nav__items flex justify-around items-center" style={{ height: '60px' }}>
          {navItems.map(({ key, path, icon: Icon, label, panel }) => {
            const isActive = panel
              ? (location.pathname === path && currentPanel === panel)
              : path === '/projects'
              ? (location.pathname === '/projects' || location.pathname.startsWith('/projects/'))
              : (path === '/chat' && Boolean(currentPanel))
                ? false
              : location.pathname === path

            const destination = panel ? `${path}?panel=${panel}` : path
            return (
              <button
                key={key}
                onClick={() => navigate(destination)}
                className={clsx(
                  'flex flex-col items-center justify-center w-full h-full transition-all duration-300 active:scale-90',
                  isActive && 'text-telegram-button'
                )}
                style={{ gap: '3px', color: isActive ? undefined : 'var(--nav-inactive-color)' }}
              >
                <div className="relative">
                  {isActive && (
                    <div
                      className="absolute -inset-2.5 rounded-xl bg-telegram-button/8 animate-fade-in"
                    />
                  )}
                  <Icon size={21} strokeWidth={isActive ? 2.2 : 1.6} className="relative" />
                </div>
                <span
                  className={clsx('transition-all duration-300', isActive && 'font-semibold text-telegram-button')}
                  style={{ fontSize: '10px' }}
                >
                  {label}
                </span>
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
